import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { enqueueSnackbar } from 'notistack';
import { QUERY_KEYS } from '../constants';
import { postData } from '../utils/api';
import { getTurnstileHeaders } from '../utils/turnstile';
import useLocalization from './useLocalization';
import { useModal } from './useDialog';
import { useTurnstile } from './useTurnstile';

export const useForgotPasswordForm = () => {
  const { t } = useLocalization();
  const { closeModal } = useModal();
  const turnstile = useTurnstile('forgot_password');
  const schema = z.object({ email: z.email(t('register.validation.invalidEmail')) });
  const { control, handleSubmit, reset, formState } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const handleClose = () => {
    reset();
    closeModal();
  };

  const { mutateAsync: forgotPassword, isPending } = useMutation({
    mutationKey: [QUERY_KEYS.forgotPassword],
    mutationFn: ({ email, turnstileToken }: { email: string; turnstileToken?: string }) =>
      postData('customer/forgot-password', { email }, 'crm', getTurnstileHeaders(turnstileToken)),
    onSuccess: () => {
      enqueueSnackbar(t('forgotPassword.emailSent'), { variant: 'success' });
      handleClose();
    },
    onError: () => {
      enqueueSnackbar(t('unexpectedError'), { variant: 'error' });
    },
  });

  return {
    t,
    control,
    errors: formState.errors,
    isSubmitting: formState.isSubmitting || isPending,
    handleClose,
    turnstile,
    onSubmit: handleSubmit(async ({ email }) => {
      await turnstile.run((turnstileToken) => forgotPassword({ email, turnstileToken }));
    }),
  };
};
